import { useEffect, useState, useCallback } from 'react';
import { clsx } from 'clsx';
import { useNotificationStore, type Notification } from '@/stores/notificationStore';

const typeStyles: Record<Notification['type'], string> = {
  info: 'bg-blue-500',
  success: 'bg-green-500',
  warning: 'bg-yellow-500',
  error: 'bg-red-500',
};

const typeIcons: Record<Notification['type'], string> = {
  info: 'ℹ',
  success: '✓',
  warning: '⚠',
  error: '✕',
};

interface ToastItemProps {
  notification: Notification;
  onDismiss: () => void;
}

function ToastItem({ notification, onDismiss }: ToastItemProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  const handleDismiss = useCallback(() => {
    setIsLeaving(true);
    setTimeout(onDismiss, 300);
  }, [onDismiss]);

  useEffect(() => {
    // Animate in
    requestAnimationFrame(() => {
      setIsVisible(true);
    });

    // Auto dismiss after 5 seconds
    const timer = setTimeout(() => {
      handleDismiss();
    }, 5000);

    return () => clearTimeout(timer);
  }, [handleDismiss]);

  return (
    <div
      className={clsx(
        'flex items-center gap-3 p-4 rounded-lg shadow-lg text-white transform transition-all duration-300 max-w-sm',
        typeStyles[notification.type],
        isVisible && !isLeaving
          ? 'translate-x-0 opacity-100'
          : 'ltr:translate-x-full rtl:-translate-x-full opacity-0'
      )}
      dir="rtl"
    >
      <div className="flex-shrink-0 text-xl">{typeIcons[notification.type]}</div>
      <div className="flex-1 min-w-0">
        <h4 className="font-semibold text-sm">{notification.title}</h4>
        <p className="text-sm opacity-90 truncate">{notification.message}</p>
      </div>
      <button
        onClick={handleDismiss}
        className="flex-shrink-0 opacity-70 hover:opacity-100"
        aria-label="סגור הודעה"
      >
        ✕
      </button>
    </div>
  );
}

export function ToastContainer() {
  const { notifications, removeNotification } = useNotificationStore();
  const [visibleToasts, setVisibleToasts] = useState<Notification[]>([]);

  useEffect(() => {
    // Show only unread notifications as toasts (max 3)
    const unread = notifications.filter((n) => !n.read).slice(0, 3);
    setVisibleToasts(unread);
  }, [notifications]);

  if (visibleToasts.length === 0) return null;

  return (
    <div
      className="fixed top-4 right-4 z-50 flex flex-col gap-2"
      role="region"
      aria-live="polite"
      aria-atomic="true"
      aria-label="התראות מערכת"
    >
      {visibleToasts.map((notification) => (
        <ToastItem
          key={notification.id}
          notification={notification}
          onDismiss={() => removeNotification(notification.id)}
        />
      ))}
    </div>
  );
}
